import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";

export function ProblemSolutionSection() {
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();

  const problems = [
    {
      title: t("landing.problems.spreadsheets.title"),
      description: t("landing.problems.spreadsheets.description"),
    },
    {
      title: t("landing.problems.tracking.title"),
      description: t("landing.problems.tracking.description"),
    },
    {
      title: t("landing.problems.reporting.title"),
      description: t("landing.problems.reporting.description"),
    },
  ];

  const solutions = [
    {
      title: t("landing.solutions.automated.title"),
      description: t("landing.solutions.automated.description"),
    },
    {
      title: t("landing.solutions.realtime.title"),
      description: t("landing.solutions.realtime.description"),
    },
    {
      title: t("landing.solutions.insights.title"),
      description: t("landing.solutions.insights.description"),
    },
  ];

  return (
    <section ref={ref} className="py-20 bg-gradient-to-b from-background to-secondary/30">
      <div className="container mx-auto px-4">
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t("landing.problemSolution.title")}</h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t("landing.problemSolution.subtitle")}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Problems */}
          <div
            className={`space-y-6 transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 -translate-x-0" : "opacity-0 -translate-x-10"
            }`}
          >
            <h3 className="text-2xl font-bold text-destructive flex items-center gap-2 mb-6">
              <AlertCircle className="w-6 h-6" />
              {t("landing.problemSolution.problemsTitle")}
            </h3>
            {problems.map((problem, index) => (
              <Card key={index} className="p-6 border-destructive/20 bg-destructive/5 hover:shadow-lg transition-all">
                <h4 className="font-semibold mb-2 flex items-start gap-2">
                  <span className="text-destructive mt-1">✗</span>
                  {problem.title}
                </h4>
                <p className="text-muted-foreground text-sm">{problem.description}</p>
              </Card>
            ))}
          </div>

          {/* Solutions */}
          <div
            className={`space-y-6 transition-all duration-700 delay-400 ${
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"
            }`}
          >
            <h3 className="text-2xl font-bold text-primary flex items-center gap-2 mb-6">
              <CheckCircle2 className="w-6 h-6" />
              {t("landing.problemSolution.solutionsTitle")}
            </h3>
            {solutions.map((solution, index) => (
              <Card key={index} className="p-6 border-primary/20 bg-primary/5 hover:shadow-lg hover:scale-105 transition-all">
                <h4 className="font-semibold mb-2 flex items-start gap-2">
                  <CheckCircle2 className="text-primary mt-1 w-5 h-5" />
                  {solution.title}
                </h4>
                <p className="text-muted-foreground text-sm">{solution.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
